import {isPlatformBrowser} from '@angular/common';
import {computed, effect, inject, Injectable, Injector, NgZone, OnDestroy, PLATFORM_ID, signal, Signal, untracked} from '@angular/core';
import {Router} from '@angular/router';
import {AlertController, ToastController} from '@ionic/angular/standalone';
import {Profile, profileConverter, TestResult} from '@models';
import {
	createUserWithEmailAndPassword,
	deleteUser,
	EmailAuthProvider,
	onAuthStateChanged,
	reauthenticateWithCredential,
	signInWithEmailAndPassword,
	signOut,
	updateEmail,
	updatePassword,
	User
} from 'firebase/auth';
import {DataSnapshot, onValue, ref, remove, update} from 'firebase/database';
import {
	collection,
	deleteDoc,
	doc,
	DocumentSnapshot,
	getDoc,
	getDocs,
	onSnapshot,
	query as queryFS,
	QuerySnapshot,
	setDoc,
	updateDoc,
	where
} from 'firebase/firestore';
import {deleteObject, getDownloadURL, getStorage, ref as storeRef, uploadString} from 'firebase/storage';
import {AuthenticationService, SubscriptionService} from '../auth';
import {logger, StorageService, TranslateHelperService} from '../shared';
import {FirebaseInitService} from '../shared/firebase-init.service';
import {NotificationService} from '../messaging';
import {OnboardingCommandService} from '../engagement';
import {BadgeService} from './badge.service';
import {ProfileCleanupService} from './profile-cleanup.service';
import {ProfileImageService} from './profile-image.service';
import {buildProfileSignature, coerceTimestamp} from './profile-utils';

@Injectable()
export class ProfileService implements OnDestroy {

	public currentUid: string = '';
	public profile = signal<Profile | undefined>(undefined);
	public profileList: Profile[] = [];
	public readonly profiles = signal<Profile[]>([]);

	public readonly badgeService = inject(BadgeService);
	private readonly subscriptionService = inject(SubscriptionService);
	private readonly notificationService = inject(NotificationService, {optional: true});
	private readonly injector = inject(Injector);

	private authentUser: User | null = null;
	private readonly unsubscribeFns: Array<() => void> = [];
	private profilesLoaded = false;
	private profilesLoadPromise?: Promise<Profile[]>;
	private readonly profileSignals = new Map<string, ReturnType<typeof signal<Profile | undefined>>>();
	private readonly profileSignalUnsubscribers = new Map<string, () => void>();
	private readonly fullProfileSignals = new Map<string, ReturnType<typeof signal<Profile>>>();
	private readonly fullProfileUnsubscribers = new Map<string, Array<() => void>>();
	private readonly authUserSignal = signal<User | null>(null);
	private readonly profileSourceSignal = signal<Signal<Profile | undefined> | null>(null);
	private lastAuthUid: string | null = null;
	private lastProfileSignature: string | null = null;
	private authSyncEffect?: {destroy: () => void};
	private profileSyncEffect?: {destroy: () => void};

	private readonly profilePath = 'profile';
	private readonly firebaseInit = inject(FirebaseInitService);
	private readonly profileImageService = inject(ProfileImageService);
	private readonly profileCleanupService = inject(ProfileCleanupService);

	// Lazy getters for Firebase services to avoid initialization errors in SSR
	private get auth() {
		return this.firebaseInit.auth;
	}

	private get storage() {
		return this.firebaseInit.storage;
	}

	private get database() {
		return this.firebaseInit.database;
	}

	private get firestore() {
		return this.firebaseInit.firestore;
	}

	private readonly platformId = inject(PLATFORM_ID);
	private readonly isBrowser = isPlatformBrowser(this.platformId);
	private readonly toastController = this.isBrowser ? inject(ToastController) : null;
	private readonly translateService: TranslateHelperService = inject(TranslateHelperService);
	private readonly localStorageService = inject(StorageService);
	private readonly ngZone: NgZone = inject(NgZone);
	private readonly router: Router = inject(Router);
	private readonly alterController = this.isBrowser ? inject(AlertController) : null;

	public incompleteProfile = computed<boolean>(() => {
		if (this.profile() === undefined || !this.profile()?.uuid) {
			return false;
		}
		return this.profile()!.username === Profile.defaultUsername;
	});

	private ensureProfileImage(profile: Profile): void {
		if (!profile || !profile.hasImage) {
			if (profile?.uuid) {
				this.profileImageService.clearCachedUrl(profile.uuid);
			}
			return;
		}
		this.profileImageService.resolveProfileImage(profile)
			.then((resolved) => {
				if (!resolved?.url || profile.imgUrl === resolved.url) {
					return;
				}
				this.updateProfileImage(profile, resolved.url, resolved.cache);
			})
			.catch(() => {
				return;
			});
	}

	private updateProfileImage(profile: Profile, url: string, cache: boolean): void {
		profile.imgUrl = url;
		if (cache) {
			this.profileImageService.setCachedUrl(profile.uuid, url);
		}
		const listIndex = this.profileList.findIndex(p => p.uuid === profile.uuid);
		if (listIndex >= 0) {
			this.profileList[listIndex].imgUrl = url;
		}
		const currentProfile = this.profile();
		if (currentProfile && currentProfile.uuid === profile.uuid) {
			this.profile.set(new Profile({...profile} as Record<string, unknown>));
		}
	}

	private getCachedProfile(uid: string): Profile | undefined {
		return this.profileList.find(p => p.uuid === uid);
	}

	private getOnboardingCommandService(): OnboardingCommandService | null {
		return this.injector.get(OnboardingCommandService, null, {optional: true});
	}

	private upsertProfileInList(profile: Profile): void {
		const listIndex = this.profileList.findIndex(p => p.uuid === profile.uuid);
		if (listIndex >= 0) {
			this.profileList[listIndex].merge(profile);
		} else {
			this.profileList.push(profile);
		}
	}

	constructor() {
		// Only initialize Firebase-dependent code in browser
		if (this.isBrowser && typeof window !== 'undefined') {
			try {
				this.profileList = [];

				const unsubscribe = onAuthStateChanged(this.auth, (user: User | null) => {
					this.ngZone.run(() => {
						this.authUserSignal.set(user);
					});
				});
				this.unsubscribeFns.push(unsubscribe);

				this.authSyncEffect = effect(() => {
					const user = this.authUserSignal();
					const nextUid = user?.uid ?? null;
					if (this.lastAuthUid !== nextUid) {
						this.lastAuthUid = nextUid;
						this.lastProfileSignature = null;
					}
					if (user) {
						this.authentUser = user;
						this.currentUid = user.uid;
						this.profileSourceSignal.set(this.getProfileByUidSubscribe(user.uid, false));
					} else {
						this.authentUser = null;
						this.currentUid = '';
						this.profile.set(undefined);
						this.profileSourceSignal.set(null);
					}
				});

				this.profileSyncEffect = effect(() => {
					const profileSignal = this.profileSourceSignal();
					if (!profileSignal) {
						return;
					}
					const profile = profileSignal();
					if (!profile?.uuid) {
						return;
					}
					const signature = this.buildProfileSignature(profile);
					if (signature === this.lastProfileSignature) {
						return;
					}
					this.lastProfileSignature = signature;
					const currentProfile = untracked(() => this.profile());
					if (currentProfile && currentProfile.uuid === profile.uuid) {
						const currentUpdatedAt = this.coerceTimestamp(currentProfile.updatedAt);
						const incomingUpdatedAt = this.coerceTimestamp(profile.updatedAt);
						if (currentUpdatedAt > 0 && incomingUpdatedAt > 0 && incomingUpdatedAt < currentUpdatedAt) {
							return;
						}
						currentProfile.merge(profile);
						this.profile.set(new Profile({...currentProfile} as Record<string, unknown>));
					} else {
						this.profile.set(profile);
					}
					this.ensureProfileImage(profile);
					this.translateService.switchLanguage(profile.lang?.shortCode ?? 'en').then();
				});

				if (this.notificationService) {
					effect(() => {
						const profile = this.profile();
						if (!profile?.uuid) {
							this.notificationService?.stopNotifications();
							return;
						}
						this.notificationService?.loadNotifications(profile);
					});
				}
			} catch {
				// Firebase not initialized (SSR scenario)
				logger.warn('ProfileService: Firebase not available, skipping initialization');
			}
		} else {
			// SSR: Initialize with empty state
			this.profileList = [];
		}
	}

	private buildProfileSignature(profile: Profile): string {
		return buildProfileSignature(profile);
	}

	private coerceTimestamp(value: unknown): number {
		return coerceTimestamp(value);
	}

	get isAuthenticated(): boolean {
		return this.authentUser !== null;
	}

	get userData(): Profile | undefined {
		return this.profile();
	}

	async updatePassword(newPassword: string) {
		const password = await this.confirmPassword();
		if (password) {
			signInWithEmailAndPassword(this.auth, this.profile()!.email, password).then(() => {
				if (this.authentUser) {
					updatePassword(this.authentUser, newPassword).then();
				}
			});
		}
		return;
	}

	async updatePasswordAndMail(newPassword: string, newEmail: string) {
		const password = await this.confirmPassword();
		if (password) {
			signInWithEmailAndPassword(this.auth, this.profile()!.email, password).then(() => {
				if (this.authentUser) {
					updateEmail(this.authentUser, newEmail).then();
					updatePassword(this.authentUser, newPassword).then();
				}
			});
		}
		return;
	}

	async updateEmail(newEmail: string) {
		const password = await this.confirmPassword();
		if (password) {
			signInWithEmailAndPassword(this.auth, this.profile()!.email, password).then(() => {
				if (this.authentUser) {
					updateEmail(this.authentUser, newEmail).then();
				}
			});
		}
		return;
	}

	createAcount(email: string, password: string) {
		return createUserWithEmailAndPassword(this.auth, email, password);
	}

	notifyObserver() {
		const current = this.profile();
		if (current) {
			this.profile.set(new Profile({...current} as Record<string, unknown>));
		}
	}

	delete(pwd: string) {
		if (!this.authentUser) {
			return;
		}
		reauthenticateWithCredential(this.authentUser, EmailAuthProvider.credential(this.profile()!.email, pwd)).then(() => {
			this.deleteProfile();
		}).catch((error) => logger.error(error));
	}

	currentUserId() {
		return this.isAuthenticated ? (this.authentUser?.uid ?? '') : '';
	}

	async update(profile?: Profile) {
		const currentProfile = profile ? profile : this.profile()!;
		currentProfile.updatedAt = new Date();
		try {
			if (!profile) {
				currentProfile.emailVerified = this.auth.currentUser?.emailVerified ?? false;
			}
			await update(ref(this.database), currentProfile.realtimeUpdate());
			await updateDoc(doc(this.firestore, this.profilePath, currentProfile.uuid).withConverter(profileConverter), {...currentProfile.firestoreUpdate()});
			if (!profile || currentProfile.uuid === this.profile()?.uuid) {
				this.notifyObserver();
			}
		} catch (e) {
			logger.error(e);
			return Promise.resolve();
		}
	}

	async updateShortDesc(shortDesc: string): Promise<void> {
		const currentProfile = this.profile();
		if (!currentProfile?.uuid) {
			return;
		}
		const updatedAt = new Date();
		currentProfile.shortDesc = shortDesc;
		currentProfile.updatedAt = updatedAt;
		try {
			const basePath = `/profile/${currentProfile.uuid}/`;
			await update(ref(this.database), {
				[`${basePath}shortDesc`]: shortDesc,
				[`${basePath}updatedAt`]: updatedAt.getTime()
			});
			await updateDoc(
				doc(this.firestore, this.profilePath, currentProfile.uuid).withConverter(profileConverter),
				{shortDesc, updatedAt: updatedAt.getTime()}
			);
			this.profile.set(new Profile({...currentProfile} as Record<string, unknown>));
		} catch (error) {
			logger.error(error);
		}
	}

	async updateDescription(description: string): Promise<void> {
		const currentProfile = this.profile();
		if (!currentProfile?.uuid) {
			return;
		}
		const updatedAt = new Date();
		currentProfile.description = description;
		currentProfile.updatedAt = updatedAt;
		try {
			const basePath = `/profile/${currentProfile.uuid}/`;
			await update(ref(this.database), {
				[`${basePath}description`]: description,
				[`${basePath}updatedAt`]: updatedAt.getTime()
			});
			await updateDoc(
				doc(this.firestore, this.profilePath, currentProfile.uuid).withConverter(profileConverter),
				{description, updatedAt: updatedAt.getTime()}
			);
			this.profile.set(new Profile({...currentProfile} as Record<string, unknown>));
		} catch (error) {
			logger.error(error);
		}
	}

	async updateTestResults(testResults: TestResult[]): Promise<void> {
		const currentProfile = this.profile();
		if (!currentProfile) {
			return;
		}
		currentProfile.test = testResults;
		try {
			await updateDoc(
				doc(this.firestore, this.profilePath, currentProfile.uuid).withConverter(profileConverter),
				{test: testResults, updatedAt: Date.now()}
			);
			this.profile.set(new Profile({...currentProfile} as Record<string, unknown>));
		} catch (error) {
			logger.error(error);
		}
	}

	observe() {
		return this.profile.asReadonly();
	}

	getFullProfile(uid: string) {
		if (this.profile()!.scope !== 'Admin' && uid !== this.profile()!.uuid) {
			throw new Error('You are not admin');
		}

		// Vérifier que l'uid n'est pas vide
		if (!uid || uid.trim() === '') {
			throw new Error('Invalid uid provided to getFullProfile');
		}

		if (this.fullProfileSignals.has(uid)) {
			return this.fullProfileSignals.get(uid)!.asReadonly();
		}

		const baseProfile = new Profile({uuid: uid, dbFilled: false});
		const profileSignal = signal<Profile>(baseProfile);
		this.fullProfileSignals.set(uid, profileSignal);
		this.fullProfileUnsubscribers.set(uid, []);
		let realTimeUpdated = false;
		let firestoreUpdated = false;

		const publishProfile = () => {
			baseProfile.dbFilled = realTimeUpdated && firestoreUpdated;
			profileSignal.set(new Profile({...baseProfile} as Record<string, unknown>));
			this.ensureProfileImage(baseProfile);
		};

		const realtimeUnsub = onValue(ref(this.database, this.profilePath + '/' + uid), (snapshot: DataSnapshot) => {
			this.ngZone.run(() => {
				if (realTimeUpdated) {
					return;
				}
				if (!snapshot.exists()) {
					realTimeUpdated = true;
					publishProfile();
					return;
				}
				const snapshotData = snapshot.val() as Record<string, unknown>;
				const hasPronoun = Object.prototype.hasOwnProperty.call(snapshotData, 'pronoun');
				const sanitizedData = {...snapshotData};
				if (sanitizedData['imgUrl'] === null || sanitizedData['imgUrl'] === undefined) {
					delete sanitizedData['imgUrl'];
				}
				if (sanitizedData['hasImage'] === null || sanitizedData['hasImage'] === undefined) {
					delete sanitizedData['hasImage'];
				}
				if (sanitizedData['pronoun'] === null || sanitizedData['pronoun'] === undefined) {
					delete sanitizedData['pronoun'];
				}
				const tempProfile = new Profile({
					                                ...sanitizedData,
					                                uuid: snapshot.key || uid
				                                });
				if (!hasPronoun) {
					(tempProfile as unknown as Record<string, unknown>)['pronoun'] = undefined;
				}
				baseProfile.merge(tempProfile);
				realTimeUpdated = true;
				publishProfile();
				this.upsertProfileInList(tempProfile);
			});
		});
		this.fullProfileUnsubscribers.get(uid)!.push(realtimeUnsub);

		getDoc(doc(this.firestore, this.profilePath, uid).withConverter(profileConverter)).then((snapshot: DocumentSnapshot<Profile>) => {
			if (!snapshot.exists() || firestoreUpdated) {
				return;
			}
			const snapshotData = snapshot.data();
			if (!snapshotData) {
				return;
			}
			const hasPracticesData =
				(snapshotData as unknown as Record<string, unknown>)['practices'] !== null &&
				(snapshotData as unknown as Record<string, unknown>)['practices'] !== undefined ||
				(snapshotData as unknown as Record<string, unknown>)['pratiques'] !== null &&
				(snapshotData as unknown as Record<string, unknown>)['pratiques'] !== undefined ||
				(snapshotData as unknown as Record<string, unknown>)['practices_objects'] !== null &&
				(snapshotData as unknown as Record<string, unknown>)['practices_objects'] !== undefined;
			const tempProfile = snapshotData;
			if (!hasPracticesData && baseProfile.practices_objects?.size) {
				tempProfile.practices_objects = new Map(baseProfile.practices_objects);
				tempProfile.practices = baseProfile.practices;
				tempProfile.pratiques = baseProfile.pratiques;
			}
			baseProfile.merge(tempProfile);
			firestoreUpdated = true;
			publishProfile();
			this.upsertProfileInList(tempProfile);
		}).catch((error) => {
			logger.error('Error fetching full profile:', error);
		});

		return profileSignal.asReadonly();
	}

	async getProfileByUid(uid: string, useCache = true): Promise<Profile | undefined> {
		// Vérifier que l'uid n'est pas vide pour éviter les références de document invalides
		if (!uid || uid.trim() === '') {
			logger.warn('getProfileByUid called with empty uid');
			return undefined;
		}

		if (useCache) {
			const cached = this.getCachedProfile(uid);
			if (cached) {
				return cached;
			}
		}

		try {
			const snapshot = await getDoc(doc(this.firestore, this.profilePath, uid).withConverter(profileConverter));
			if (!snapshot.exists()) {
				return undefined;
			}
			const snapshotData = snapshot.data();
			if (!snapshotData) {
				return undefined;
			}
			const baseProfile = new Profile({uuid: uid});
			const tempProfile = snapshotData;
			tempProfile.dbFilled = false;
			baseProfile.merge(tempProfile);
			this.upsertProfileInList(tempProfile);
			return baseProfile;
		} catch (error) {
			logger.error('Error fetching profile:', error);
			return undefined;
		}
	}

	getProfileByUidSubscribe(uid: string, useCache = true) {
		// Vérifier que l'uid n'est pas vide pour éviter les références de document invalides
		if (!uid || uid.trim() === '') {
			logger.warn('getProfileByUidSubscribe called with empty uid');
			return signal<Profile | undefined>(undefined).asReadonly();
		}

		if (this.profileSignals.has(uid)) {
			return this.profileSignals.get(uid)!.asReadonly();
		}

		const profileSignal = signal<Profile | undefined>(undefined);
		this.profileSignals.set(uid, profileSignal);

		if (useCache) {
			const cached = this.getCachedProfile(uid);
			if (cached) {
				profileSignal.set(cached);
			}
		}

		const unsubscribe = onSnapshot(
			doc(this.firestore, this.profilePath, uid).withConverter(profileConverter),
			(snapshot: DocumentSnapshot<Profile>) => {
				this.ngZone.run(() => {
					if (!snapshot.exists()) {
						return;
					}
					const snapshotData = snapshot.data();
					if (!snapshotData) {
						return;
					}
					const profileDb = snapshotData;
					profileDb.dbFilled = false;

					profileSignal.set(profileDb);
					this.upsertProfileInList(profileDb);
				});
			},
			(error) => {
				logger.error('Error in profile snapshot:', error);
			}
		);
		this.profileSignalUnsubscribers.set(uid, unsubscribe);
		return profileSignal.asReadonly();
	}

	async createProfile(newProfile: Profile) {
		try {
			return await setDoc(doc(this.firestore, this.profilePath, newProfile.uuid), {
				date: newProfile.date,
				createdAt: Date.now(),
				email: newProfile.email,
				emailVerified: false
			});
		} catch (message_1) {
			logger.error(message_1);
			return false;
		}
	}

	ResetPassword(authentService: AuthenticationService, email: string) {
		return authentService.passwordRecover(email);
	}

	blockUser(profile_uid: string) {
		void this.getProfileByUid(profile_uid).then(async (user) => {
			if (!user?.uuid) {
				return;
			}
			const toastController = this.toastController;
			if (!toastController) {
				return;
			}
			toastController.create({
				                            message: await this.translateService.instant('profile.Do you really want to block this user'),
				                            buttons: [
					                            {
						                            text: await this.translateService.instant('yes'),
						                            role: 'accepte',
						                            handler: () => {
							                            this.profile()!.blocked.push(profile_uid);
							                            this.update().then(async () => {
								                            toastController
								                                .create({
									                                        message: (await this.translateService.instant('profile.You blocked [user_name]')).replace('[user_name]', user.username),
									                                        duration: 3000,
									                                        cssClass: 'yourCssClassName errorToast'
								                                        }).then(t => t.present());
							                            });
							                            this.notifyObserver();
						                            }
					                            },
					                            {
						                            text: await this.translateService.instant('cancel'),
						                            role: 'cancel'
					                            }
				                            ],
				                            cssClass: 'yourCssClassName successToast'
			                            }).then(t => t.present());
		}).catch((error: unknown) => logger.error(error));
	}

	addUserFavorit(profile_uid: string) {
		const currentProfile = this.profile();
		if (!currentProfile) {
			return;
		}
		if (currentProfile.favorits?.includes(profile_uid)) {
			return;
		}
		currentProfile.favorits.push(profile_uid);
		this.update()?.then();
		void this.getOnboardingCommandService()?.completeFirstInteraction('favorite');
		void this.getProfileByUid(profile_uid).then(async (user) => {
			if (user?.uuid) {
				const toastController = this.toastController;
				if (!toastController) {
					return;
				}
				const message = (await this.translateService.instant('profile.adding user favorit'))
					.replace('[user_name]', user.username);
				toastController
				    .create({
					            message,
					            duration: 3000,
					            color: 'success',
					            cssClass: 'yourCssClassName successToast'
				            }
				    ).then(t => t.present());
			}
		}).catch((error: unknown) => logger.error(error));
	}

	unblockUser(uid: string) {
		const index = this.profile()!.blocked.indexOf(uid, 0);
		if (index > -1) {
			this.profile()!.blocked.splice(index, 1);
		}
		this.update().then(async () => {
			const toastController = this.toastController;
			if (!toastController) {
				return;
			}
			return toastController
			           .create({
				                   message: await this.translateService.instant('profile.remove user block list'),
				                   duration: 3000,
				                   color: 'success',
				                   cssClass: 'yourCssClassName'
			                   }).then(t => t.present);
		});
	}

	async removeUserFavorit(uid: string) {
		const index = this.profile()!.favorits.indexOf(uid, 0);
		if (index > -1) {
			this.profile()!.favorits.splice(index, 1);
		}
		this.update()?.then();

		const toastController = this.toastController;
		if (!toastController) {
			return;
		}
		const toast = await toastController
		                        .create({
			                                message: await this.translateService.instant('profile.remove user favorit'),
			                                duration: 3000,
			                                color: 'success',
			                                cssClass: 'yourCssClassName'
		                                });
		return await toast.present();
	}

	public Disconnect() {
		this.localStorageService.set('isLoggin', null);
		this.localStorageService.set('uid', null);
		return signOut(this.auth);
	}

	adminDelete(profile: Profile) {
		if (this.profile()!.scope === 'Admin') {
			this.profileCleanupService.clearUserData(profile.uuid).then((async () => {
				const realWaiter = await remove(ref(this.database, `${this.profilePath}/${profile.uuid}`));
				const fireWaiter = await deleteDoc(doc(this.firestore, this.profilePath, profile.uuid));
				return Promise.allSettled([fireWaiter, realWaiter]);
			}));
		}
	}

	getAllProfilesOnce() {
		if (this.profilesLoaded) {
			return Promise.resolve(this.profileList);
		}
		if (this.profilesLoadPromise) {
			return this.profilesLoadPromise;
		}
		this.profilesLoadPromise = getDocs(collection(this.firestore, this.profilePath).withConverter(profileConverter))
			.then((querySnapshot: QuerySnapshot<Profile>) => {
				querySnapshot.forEach((docSnapshot) => {
					const profile = docSnapshot.data();
					if (profile.username === 'SlaveousUser' || !profile.username) {
						return;
					}
					profile.dbFilled = true;
					this.upsertProfileInList(profile);
				});
			})
			.then(() => {
				this.profileList = [...new Set(this.profileList)];
				this.profiles.set(this.profileList);
				this.profilesLoaded = true;
				return this.profileList;
			})
			.finally(() => {
				this.profilesLoadPromise = undefined;
			});

		return this.profilesLoadPromise;
	}

	updateColor(color: string) {
		this.profile()!.color = color;
		this.update()?.then();
		this.notifyObserver();
	}

	async getProfileByUsername(userName: string): Promise<Profile | undefined> {
		const cached = this.profileList.find(p => p.username === userName);
		if (cached) {
			return cached;
		}

		const q = queryFS(collection(this.firestore, this.profilePath), where('username', '==', userName));
		const querySnapshot = await getDocs(q);
		if (querySnapshot.empty) {
			return undefined;
		}
		const firstDoc = querySnapshot.docs[0];
		const docData = firstDoc.data();
		if (!docData) {
			return undefined;
		}
		const newprofile = new Profile({uuid: firstDoc.id, ...docData});
		newprofile.dbFilled = true;
		this.upsertProfileInList(newprofile);
		return newprofile;
	}

	async updateProfilePicture(dataUrl: string, onStatus?: (status: string) => void): Promise<void> {
		onStatus?.('started');
		const snapshot = await uploadString(
			storeRef(getStorage(), `users-gallery/${this.currentUid}/profilePicture.jpeg`),
			dataUrl,
			'data_url',
			{
				cacheControl: 'public,max-age=31536000,immutable'
			}
		);
		onStatus?.('uploaded');
		const downloadRUL = await getDownloadURL(snapshot.ref);
		this.profile()!.imgUrl = downloadRUL;
		this.profileImageService.setCachedUrl(this.currentUid, downloadRUL);
		await this.update();
		onStatus?.('finished');
	}

	async listGalleryPhotos(uid: string): Promise<string[]> {
		const profile = await this.getProfileByUid(uid);
		if (!profile?.galleryUrls || !Array.isArray(profile.galleryUrls)) {
			return [];
		}
		return profile.galleryUrls.filter((u): u is string => !!u);
	}

	async uploadGalleryPhoto(uid: string, slotIndex: number, dataUrl: string): Promise<void> {
		const profile = await this.getProfileByUid(uid, false);
		if (!profile) {
			throw new Error('Profile not found');
		}
		const maxSlots = this.subscriptionService.getMaxGallerySlots(profile);
		if (maxSlots <= 0) {
			throw new Error('No gallery slots available for this subscription');
		}
		const currentUrls = profile.galleryUrls && Array.isArray(profile.galleryUrls) ? profile.galleryUrls : [];
		if (slotIndex < 0 || slotIndex >= maxSlots) {
			throw new Error('Invalid slot index');
		}
		const storageRef = storeRef(this.storage, `users-gallery/${uid}/gallery_${slotIndex}.jpeg`);
		const snapshot = await uploadString(storageRef, dataUrl, 'data_url', {
			cacheControl: 'public,max-age=31536000,immutable'
		});
		const downloadUrl = await getDownloadURL(snapshot.ref);
		const newUrls = [...currentUrls];
		while (newUrls.length <= slotIndex) {
			newUrls.push('');
		}
		newUrls[slotIndex] = downloadUrl;
		while (newUrls.length < maxSlots) {
			newUrls.push('');
		}
		profile.galleryUrls = newUrls;
		if (profile.uuid === this.currentUid && this.profile()?.uuid === uid) {
			this.profile.set(new Profile({...profile} as Record<string, unknown>));
		}
		await this.update(profile);
		this.upsertProfileInList(profile);
	}

	async deleteGalleryPhoto(uid: string, slotIndex: number): Promise<void> {
		const profile = await this.getProfileByUid(uid, false);
		if (!profile) {
			throw new Error('Profile not found');
		}
		const currentUrls = profile.galleryUrls && Array.isArray(profile.galleryUrls) ? profile.galleryUrls : [];
		if (slotIndex < 0 || slotIndex >= currentUrls.length) {
			return;
		}
		const storageRef = storeRef(this.storage, `users-gallery/${uid}/gallery_${slotIndex}.jpeg`);
		await deleteObject(storageRef).catch(() => {});
		const newUrls = [...currentUrls];
		newUrls[slotIndex] = '';
		profile.galleryUrls = newUrls;
		if (profile.uuid === this.currentUid && this.profile()?.uuid === uid) {
			this.profile.set(new Profile({...profile} as Record<string, unknown>));
		}
		await this.update(profile);
		this.upsertProfileInList(profile);
	}

	deleteProfile() {
		const uid = this.currentUid;
		this.profileCleanupService.clearUserData(uid).then((async () => {
			const realWaiter = await remove(ref(this.database, `${this.profilePath}/${uid}`));
			const fireWaiter = await deleteDoc(doc(this.firestore, this.profilePath, uid));
			Promise.allSettled([fireWaiter, realWaiter]).then(() => {
				if (this.authentUser) {
					deleteUser(this.authentUser).then(() => {
						this.localStorageService.set('isLoggin', null);
						this.localStorageService.set('uid', null);
						return signOut(this.auth).then(() => this.router.navigateByUrl(this.translateService.localize('/app/home')));
					});
				}
			});
		}));
	}

	ngOnDestroy(): void {
		this.authSyncEffect?.destroy();
		this.profileSyncEffect?.destroy();
		this.unsubscribeFns.forEach((unsubscribe) => unsubscribe());
		this.profileSignalUnsubscribers.forEach((unsubscribe) => unsubscribe());
		this.fullProfileUnsubscribers.forEach((list) => list.forEach((unsubscribe) => unsubscribe()));
	}

	updateDbModel(profile: Profile): Promise<void[]> {
		const promises: Promise<void>[] = [];
		promises.push(update(ref(this.database), profile.fullRealtimeUpdate()));
		promises.push(updateDoc(doc(this.firestore, this.profilePath, profile.uuid).withConverter(profileConverter), {...profile}));
		return Promise.all(promises);
	}

	async switchLang(lang: string): Promise<void> {
		this.profile()!.lang.shortCode = lang;
		const result = await this.update();
		this.notifyObserver();
		return result;
	}

	setDefaultLang(lang: string): void {
		if (this.profile() && this.profile()?.lang.shortCode !== lang) {
			this.profile()!.lang.shortCode = lang;
			this.update();
			this.notifyObserver();
		}
	}

	private async confirmPassword() {
		if (!this.alterController) {
			return undefined;
		}
		const alert = await this.alterController.create({
			                                                header: await this.translateService.instant('Confirm your password'),
			                                                message: await this.translateService.instant('Please enter your current password to confirm your changes'),
			                                                inputs: [{
				                                                type: 'password'
			                                                }],
			                                                buttons: [
				                                                {
					                                                text: await this.translateService.instant('Cancel'),
					                                                role: 'cancel'
				                                                },
				                                                {
					                                                text: await this.translateService.instant('Confirm'),
					                                                role: 'confirm'
				                                                }]
		                                                });
		await alert.present();
		const {data, role} = await alert.onWillDismiss();

		if (role === 'confirm') {
			return data.values[0];
		} else {
			const canceled = await this.alterController.create({
				                                                   header: await this.translateService.instant('Changes not taken into account'),
				                                                   message: await this.translateService.instant('You have not confirmed your password, so the changes are cancelled.'),
				                                                   buttons: [
					                                                   {
						                                                   text: await this.translateService.instant('Cancel'),
						                                                   role: 'cancel'
					                                                   },
					                                                   {
						                                                   text: await this.translateService.instant('Confirm'),
						                                                   role: 'confirm'
					                                                   }]
			                                                   });
			return await canceled.present();
		}
	}
}
